/**
 * Ein Eintrag in der Kontext-Sidebar — eine gespeicherte Ansicht.
 *
 * **Fläche statt Trennlinie.** Der Zustand sitzt im Hintergrund und im Radius,
 * nicht in einem Strich zwischen den Zeilen; eine Liste mit Linien unter jeder
 * Zeile liest sich als Tabelle, nicht als Navigation.
 *
 * **`isActive` kommt von außen.** Ob eine Ansicht die gerade gezeigte ist,
 * hängt an den Suchparametern der Route (`?overdue=1`, `?tab=…`), nicht am
 * Pfad allein — das kann diese Komponente nicht selbst entscheiden, ohne die
 * Bereiche zu kennen. Sie bleibt darstellend, `router.tsx` entscheidet.
 *
 * **Der Zähler kommt vom Server**, nie aus einem `.filter().length` im Client
 * (ADR 0011: „Kein Bereich ohne eigenen Zähl-Endpunkt"). Fehlt er noch oder
 * schlägt die Abfrage fehl, steht dort nichts — eine Ansicht ohne Zahl ist
 * brauchbar, eine mit falscher Zahl nicht.
 */

import { Link, type LinkProps } from '@tanstack/react-router'

interface SidebarItemProps {
  /**
   * Ziel und Suchparameter werden **vom Router abgeleitet** (`LinkProps`),
   * nicht als `string`/`Record` abgeschrieben: Mit
   * `exactOptionalPropertyTypes` lehnt `<Link>` ein weiter gefasstes `search`
   * ab, und eine von Hand gepflegte Kopie des Suchtyps veraltet beim nächsten
   * Reiter. So bleibt ein Tippfehler im Zielzustand ein Typfehler.
   *
   * `NonNullable`, weil `LinkProps` beide Felder optional führt — unter
   * `exactOptionalPropertyTypes` ist ein durchgereichtes `undefined` etwas
   * anderes als ein fehlendes Feld, und `<Link>` nimmt es nicht an.
   */
  readonly to: NonNullable<LinkProps['to']>
  /**
   * Der Zustand, den diese Ansicht herstellt — Pflicht, denn genau das
   * unterscheidet eine gespeicherte Ansicht von einem bloßen Link.
   */
  readonly search: NonNullable<LinkProps['search']>
  readonly label: string
  readonly testId: string
  /**
   * Was in dieser Ansicht liegt. `undefined` heißt „noch nicht bekannt" und
   * zeigt nichts; `0` wird angezeigt, denn eine leere Ansicht ist eine
   * Information — anders als an der Symbolleiste, wo eine 0 nichts zu melden
   * hat und deshalb verschwindet (`Rail.tsx`).
   */
  readonly count?: number
  /**
   * Hebt den Zähler als Warnung hervor — nur für Ansichten, die einen
   * **Status** tragen (überfällig). Farbe ist nie das einzige Signal: Das
   * Wort steht daneben (`design-system`, „Farbe").
   */
  readonly isAlert?: boolean
  readonly isActive: boolean
}

export function SidebarItem({ to, search, label, testId, count, isAlert = false, isActive }: SidebarItemProps) {
  return (
    <li>
      <Link
        to={to}
        search={search}
        data-testid={testId}
        // `exact`: Der Router setzt `aria-current` sonst an jeden Eintrag, dessen Suche nur
        // *teilweise* trifft (leere Suche trifft jede) und überschreibt unser `isActive`.
        activeOptions={{ exact: true }}
        aria-current={isActive ? 'page' : undefined}
        className={`rounded-card text-body mx-1 flex h-(--row-h) items-center gap-2 px-2 transition-colors duration-(--duration-fast) ease-(--ease-out) ${
          isActive ? 'bg-accent/10 text-accent font-medium' : 'text-text hover:bg-bg'
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count !== undefined && <SidebarCount count={count} isAlert={isAlert} />}
      </Link>
    </li>
  )
}

function SidebarCount({ count, isAlert }: { count: number; isAlert: boolean }) {
  // Der Warnton erscheint erst ab einem echten Wert: Eine rote 0 wäre ein
  // Alarm ohne Anlass.
  if (isAlert && count > 0) {
    return (
      <span data-numeric className="bg-status-overdue text-label rounded-card px-1.5 font-medium text-white">
        {count}
      </span>
    )
  }

  return (
    <span data-numeric className="text-label text-muted">
      {count}
    </span>
  )
}
